import { Fragment, useCallback, useEffect, useState } from "react";
import { Building2, Copy, Pencil, Plus, QrCode, Tablet, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { defaultKioskApiUrl } from "@/lib/kioskQr";
import { fmtFecha } from "@/lib/format";
import DireccionEspanaFields from "@/components/DireccionEspanaFields";
import KioskQrCode from "@/components/KioskQrCode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function LogoEmpresa({ url, className = "h-8 w-8" }) {
  const [error, setError] = useState(false);
  useEffect(() => { setError(false); }, [url]);
  if (!url || error) {
    return (
      <div className={`flex ${className} items-center justify-center rounded-lg bg-zinc-100 text-zinc-600`}>
        <Building2 className="h-4 w-4" strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      className={`${className} rounded-lg border border-zinc-200 object-contain bg-white p-0.5`}
      onError={() => setError(true)}
    />
  );
}

const VACIO_DIRECCION = { direccion: "", poblacion: "", provincia: "", codigo_postal: "" };

const formVacio = () => ({
  nombre: "",
  cif: "",
  ...VACIO_DIRECCION,
  telefono_contacto: "",
  max_usuarios: 50,
  vigencia_inicio: "",
  vigencia_fin: "",
  admin_email: "",
  admin_password: "",
  plantilla_colores_id: "",
});

export default function Empresas() {
  const [empresas, setEmpresas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    nombre: "", cif: "", ...VACIO_DIRECCION, telefono_contacto: "", max_usuarios: 50,
    gestionar_zonas: false, gestionar_proyectos: false, gestionar_gastos: false, gestionar_rutas_comerciales: false, gestionar_gerencia: false,
  });
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState(formVacio());
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [adminFor, setAdminFor] = useState(null);
  const [adminForm, setAdminForm] = useState({ email: "", password: "", nombre: "", apellidos: "" });
  const [plantillas, setPlantillas] = useState([]);
  const [kioskFor, setKioskFor] = useState(null);
  const [kioskData, setKioskData] = useState(null);
  const [kioskApiUrl, setKioskApiUrl] = useState(() => defaultKioskApiUrl());
  const [cargandoKiosk, setCargandoKiosk] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [empRes, plRes] = await Promise.all([
        api.get("/empresas"),
        api.get("/plantillas-colores"),
      ]);
      setEmpresas(empRes.data);
      setPlantillas(plRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const crear = async () => {
    try {
      await api.post("/empresas", form);
      toast.success("Empresa creada con periodo de uso de 1 año");
      setOpen(false);
      setForm({
        nombre: "", cif: "", ...VACIO_DIRECCION, telefono_contacto: "", max_usuarios: 50,
        gestionar_zonas: false, gestionar_proyectos: false, gestionar_gastos: false, gestionar_rutas_comerciales: false, gestionar_gerencia: false,
      });
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirEdicion = async (emp) => {
    try {
      const { data } = await api.get(`/empresas/${emp.id}`);
      setEditFor(data);
      setEditForm({
        nombre: data.nombre || "",
        cif: data.cif || "",
        direccion: data.direccion || "",
        poblacion: data.poblacion || "",
        provincia: data.provincia || "",
        codigo_postal: data.codigo_postal || "",
        telefono_contacto: data.telefono_contacto || "",
        max_usuarios: data.max_usuarios || 50,
        vigencia_inicio: data.vigencia_inicio || "",
        vigencia_fin: data.vigencia_fin || "",
        admin_email: data.admin_email || "",
        admin_password: "",
        plantilla_colores_id: data.plantilla_colores_id ? String(data.plantilla_colores_id) : "",
      });
      setLogoFile(null);
      setLogoPreview(data.logo_url || null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cerrarEdicion = () => {
    setEditFor(null);
    setLogoFile(null);
    setLogoPreview(null);
    setEditForm(formVacio());
  };

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const guardarEdicion = async () => {
    try {
      const emailActual = (editFor?.admin_email || "").toLowerCase().trim();
      const emailNuevo = (editForm.admin_email || "").toLowerCase().trim();
      const cambiaAdmin = Boolean(emailNuevo && emailNuevo !== emailActual);
      const gerenciaActiva = !!editFor?.gestionar_gerencia;

      if (cambiaAdmin && gerenciaActiva && !editForm.admin_password) {
        toast.error("Indica la contraseña del nuevo administrador");
        return;
      }

      const payload = {
        nombre: editForm.nombre,
        cif: editForm.cif,
        direccion: editForm.direccion,
        poblacion: editForm.poblacion,
        provincia: editForm.provincia,
        codigo_postal: editForm.codigo_postal,
        telefono_contacto: editForm.telefono_contacto,
        max_usuarios: Number(editForm.max_usuarios),
        vigencia_inicio: editForm.vigencia_inicio,
        vigencia_fin: editForm.vigencia_fin,
      };
      if (editForm.admin_email) payload.admin_email = editForm.admin_email;
      if (editForm.admin_password) payload.admin_password = editForm.admin_password;
      payload.plantilla_colores_id = editForm.plantilla_colores_id
        ? Number(editForm.plantilla_colores_id)
        : null;

      await api.put(`/empresas/${editFor.id}`, payload);

      if (logoFile) {
        const fd = new FormData();
        fd.append("logo", logoFile);
        await api.post(`/empresas/${editFor.id}/logo`, fd);
      }

      toast.success(
        cambiaAdmin && gerenciaActiva
          ? "Administrador transferido. El anterior conserva su correo como trabajador."
          : "Empresa actualizada"
      );
      cerrarEdicion();
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleActiva = async (emp) => {
    try {
      await api.put(`/empresas/${emp.id}`, { activa: !emp.activa });
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleZonas = async (emp) => {
    try {
      await api.put(`/empresas/${emp.id}`, { gestionar_zonas: !emp.gestionar_zonas });
      toast.success(
        emp.gestionar_zonas ? "Gestión de zonas desactivada" : "Gestión de zonas activada"
      );
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleProyectos = async (emp) => {
    try {
      await api.put(`/empresas/${emp.id}`, { gestionar_proyectos: !emp.gestionar_proyectos });
      toast.success(
        emp.gestionar_proyectos ? "Módulo de proyectos desactivado" : "Módulo de proyectos activado"
      );
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleGastos = async (emp) => {
    try {
      await api.put(`/empresas/${emp.id}`, { gestionar_gastos: !emp.gestionar_gastos });
      toast.success(
        emp.gestionar_gastos ? "Módulo de gastos desactivado" : "Módulo de gastos activado"
      );
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleRutas = async (emp) => {
    try {
      await api.put(`/empresas/${emp.id}`, { gestionar_rutas_comerciales: !emp.gestionar_rutas_comerciales });
      toast.success(
        emp.gestionar_rutas_comerciales
          ? "Gestión de rutas comerciales desactivada"
          : "Gestión de rutas comerciales activada"
      );
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleGerencia = async (emp) => {
    try {
      await api.put(`/empresas/${emp.id}`, { gestionar_gerencia: !emp.gestionar_gerencia });
      toast.success(
        emp.gestionar_gerencia ? "Módulo Grupo Gerencia desactivado" : "Módulo Grupo Gerencia activado"
      );
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const crearAdmin = async () => {
    try {
      await api.post(`/empresas/${adminFor.id}/admin`, adminForm);
      toast.success(`Administrador creado para ${adminFor.nombre}`);
      setAdminFor(null);
      setAdminForm({ email: "", password: "", nombre: "", apellidos: "" });
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirKiosk = async (emp) => {
    setKioskFor(emp);
    setKioskData(null);
    setCargandoKiosk(true);
    try {
      const { data } = await api.get(`/empresas/${emp.id}/quiosco`);
      setKioskData(data);
      if (data?.api_url) setKioskApiUrl(data.api_url);
      else setKioskApiUrl(defaultKioskApiUrl());
    } catch (e) {
      toast.error(formatApiError(e));
      setKioskFor(null);
    } finally {
      setCargandoKiosk(false);
    }
  };

  const cerrarKiosk = () => {
    setKioskFor(null);
    setKioskData(null);
  };

  const copiarKiosk = async (texto, label = "URL") => {
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(`${label} copiada`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const kioskWebUrl =
    kioskData?.token
      ? `${window.location.origin.replace(/\/$/, "")}/quiosco/${kioskData.token}`
      : "";

  return (
    <div className="space-y-6" data-testid="empresas-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Empresas</h1>
          <p className="mt-1 text-sm text-zinc-500">Gestiona las empresas de la plataforma.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="crear-empresa-btn" className="gap-1.5 rounded-lg bg-zinc-950 hover:bg-zinc-800">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Nueva empresa
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white shadow-sm">
        <table className="w-full min-w-[640px] text-sm" data-testid="empresas-tabla">
          <thead>
            <tr className="border-b border-zinc-200 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500">
              <th className="px-4 py-3">Empresa</th>
              <th className="px-3 py-3">CIF</th>
              <th className="px-3 py-3">Usuarios</th>
              <th className="px-3 py-3">Vigencia</th>
              <th className="px-3 py-3">Activa</th>
              <th className="sticky right-0 bg-white px-4 py-3 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {empresas.map((emp) => (
              <Fragment key={emp.id}>
                <tr className="group border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <LogoEmpresa url={emp.logo_url ? `${emp.logo_url}?v=${emp.id}` : null} />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium">{emp.nombre}</span>
                          {emp.vigencia_expirada && (
                            <Badge variant="destructive" className="text-[10px]">Expirada</Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-400">Alta {fmtFecha(emp.created_at)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-zinc-500">{emp.cif || "—"}</td>
                  <td className="px-3 py-3">
                    <Badge variant="outline" className="border-zinc-200 bg-zinc-50 text-zinc-600">
                      {emp.num_usuarios} / {emp.max_usuarios}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-zinc-500">
                    {emp.vigencia_fin ? fmtFecha(emp.vigencia_fin) : "—"}
                  </td>
                  <td className="px-3 py-3">
                    <Switch
                      checked={emp.activa}
                      onCheckedChange={() => toggleActiva(emp)}
                      data-testid={`empresa-activa-${emp.id}`}
                    />
                  </td>
                  <td className="sticky right-0 bg-white px-4 py-3 text-right shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.12)] group-hover:bg-zinc-50">
                    <div className="flex flex-nowrap justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => abrirEdicion(emp)}
                        data-testid={`editar-empresa-${emp.id}`}
                        className="gap-1.5 rounded-lg whitespace-nowrap"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAdminFor(emp)}
                        data-testid={`crear-admin-${emp.id}`}
                        className="gap-1.5 rounded-lg whitespace-nowrap"
                      >
                        <UserPlus className="h-3.5 w-3.5" strokeWidth={1.5} />
                        Crear admin
                      </Button>
                    </div>
                  </td>
                </tr>
                <tr className="group border-b border-zinc-100 bg-zinc-50/60 last:border-0 hover:bg-zinc-50">
                  <td colSpan={6} className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400 shrink-0">
                        Módulos
                      </span>
                      <label className="flex items-center gap-2 text-xs text-zinc-600" title="Planificación por zonas (hostelería)">
                        <Switch
                          checked={!!emp.gestionar_zonas}
                          onCheckedChange={() => toggleZonas(emp)}
                          data-testid={`empresa-zonas-${emp.id}`}
                        />
                        Zonas
                      </label>
                      <label className="flex items-center gap-2 text-xs text-zinc-600" title="Proyectos y asignaciones">
                        <Switch
                          checked={!!emp.gestionar_proyectos}
                          onCheckedChange={() => toggleProyectos(emp)}
                          data-testid={`empresa-proyectos-${emp.id}`}
                        />
                        Proyectos
                      </label>
                      <label className="flex items-center gap-2 text-xs text-zinc-600" title="Tickets y facturas de gastos">
                        <Switch
                          checked={!!emp.gestionar_gastos}
                          onCheckedChange={() => toggleGastos(emp)}
                          data-testid={`empresa-gastos-${emp.id}`}
                        />
                        Gastos
                      </label>
                      <label className="flex items-center gap-2 text-xs text-zinc-600" title="Rutas y visitas comerciales">
                        <Switch
                          checked={!!emp.gestionar_rutas_comerciales}
                          onCheckedChange={() => toggleRutas(emp)}
                          data-testid={`empresa-rutas-${emp.id}`}
                        />
                        Rutas
                      </label>
                      <label className="flex items-center gap-2 text-xs text-zinc-600" title="Grupo Gerencia: el admin de la empresa configura aprobador y miembros">
                        <Switch
                          checked={!!emp.gestionar_gerencia}
                          onCheckedChange={() => toggleGerencia(emp)}
                          data-testid={`empresa-gerencia-${emp.id}`}
                        />
                        Gerencia
                      </label>
                      {emp.kiosk_activo ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => abrirKiosk(emp)}
                          data-testid={`empresa-kiosk-${emp.id}`}
                          className="h-7 gap-1.5 rounded-lg border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"
                        >
                          <Tablet className="h-3.5 w-3.5" strokeWidth={1.5} />
                          Kiosko / App Android
                        </Button>
                      ) : (
                        <span className="text-[11px] text-zinc-400" title="El admin de la empresa lo activa en Ajustes → Quiosco">
                          Kiosko inactivo
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              </Fragment>
            ))}
            {empresas.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-zinc-400">No hay empresas todavía.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="empresa-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Nueva empresa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="empresa-nombre-input" placeholder="Mi Empresa S.L." />
            </div>
            <div className="space-y-1.5">
              <Label>CIF</Label>
              <Input value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} data-testid="empresa-cif-input" placeholder="B12345678" />
            </div>
            <div className="space-y-1.5">
              <Label>Dirección (calle, número…)</Label>
              <Input value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Calle, número, nave…" />
            </div>
            <DireccionEspanaFields
              value={form}
              onChange={(next) => setForm({
                ...form,
                codigo_postal: next.codigo_postal || "",
                poblacion: next.poblacion || "",
                provincia: next.provincia || "",
              })}
            />
            <div className="space-y-1.5">
              <Label>Teléfono de contacto</Label>
              <Input value={form.telefono_contacto} onChange={(e) => setForm({ ...form, telefono_contacto: e.target.value })} placeholder="600123456" />
            </div>
            <div className="space-y-1.5">
              <Label>Número máximo de usuarios</Label>
              <Input type="number" min={1} value={form.max_usuarios} onChange={(e) => setForm({ ...form, max_usuarios: e.target.value })} />
            </div>
            <div className="space-y-3 rounded-lg border border-zinc-200 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Módulos</p>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="empresa-zonas-switch" className="text-sm">Gestión de zonas</Label>
                  <p className="text-xs text-zinc-500">Planificación por ubicaciones (hostelería)</p>
                </div>
                <Switch
                  id="empresa-zonas-switch"
                  checked={form.gestionar_zonas}
                  onCheckedChange={(v) => setForm({ ...form, gestionar_zonas: v })}
                  data-testid="empresa-zonas-switch"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="empresa-proyectos-switch" className="text-sm">Módulo de proyectos</Label>
                  <p className="text-xs text-zinc-500">Proyectos y asignaciones de trabajadores</p>
                </div>
                <Switch
                  id="empresa-proyectos-switch"
                  checked={form.gestionar_proyectos}
                  onCheckedChange={(v) => setForm({ ...form, gestionar_proyectos: v })}
                  data-testid="empresa-proyectos-switch"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="empresa-gastos-switch" className="text-sm">Módulo de gastos</Label>
                  <p className="text-xs text-zinc-500">Tickets y facturas (independiente de proyectos)</p>
                </div>
                <Switch
                  id="empresa-gastos-switch"
                  checked={form.gestionar_gastos}
                  onCheckedChange={(v) => setForm({ ...form, gestionar_gastos: v })}
                  data-testid="empresa-gastos-switch"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="empresa-rutas-switch" className="text-sm">Gestión de rutas comerciales</Label>
                  <p className="text-xs text-zinc-500">Visitas a clientes, mapa y seguimiento diario</p>
                </div>
                <Switch
                  id="empresa-rutas-switch"
                  checked={form.gestionar_rutas_comerciales}
                  onCheckedChange={(v) => setForm({ ...form, gestionar_rutas_comerciales: v })}
                  data-testid="empresa-rutas-switch"
                />
              </div>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <Label htmlFor="empresa-gerencia-switch" className="text-sm">Módulo Grupo Gerencia</Label>
                  <p className="text-xs text-zinc-500">El admin de la empresa configura aprobador y miembros</p>
                </div>
                <Switch
                  id="empresa-gerencia-switch"
                  checked={form.gestionar_gerencia}
                  onCheckedChange={(v) => setForm({ ...form, gestionar_gerencia: v })}
                  data-testid="empresa-gerencia-switch"
                />
              </div>
            </div>
            <p className="text-xs text-zinc-500">El periodo de uso se establecerá automáticamente a 1 año desde hoy.</p>
          </div>
          <DialogFooter>
            <Button onClick={crear} disabled={!form.nombre.trim()} data-testid="empresa-guardar-btn" className="bg-zinc-950 hover:bg-zinc-800">
              Crear empresa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editFor} onOpenChange={(v) => !v && cerrarEdicion()}>
        <DialogContent data-testid="editar-empresa-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Editar empresa — {editFor?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={editForm.nombre} onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })} data-testid="edit-empresa-nombre" />
            </div>
            <div className="space-y-1.5">
              <Label>Dirección (calle, número…)</Label>
              <Input value={editForm.direccion} onChange={(e) => setEditForm({ ...editForm, direccion: e.target.value })} data-testid="edit-empresa-direccion" placeholder="Calle, número, nave…" />
            </div>
            <DireccionEspanaFields
              value={editForm}
              onChange={(next) => setEditForm({
                ...editForm,
                codigo_postal: next.codigo_postal || "",
                poblacion: next.poblacion || "",
                provincia: next.provincia || "",
              })}
            />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>CIF</Label>
                <Input value={editForm.cif} onChange={(e) => setEditForm({ ...editForm, cif: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Teléfono de contacto</Label>
                <Input value={editForm.telefono_contacto} onChange={(e) => setEditForm({ ...editForm, telefono_contacto: e.target.value })} data-testid="edit-empresa-telefono" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Número máximo de usuarios</Label>
              <Input type="number" min={1} value={editForm.max_usuarios} onChange={(e) => setEditForm({ ...editForm, max_usuarios: e.target.value })} data-testid="edit-empresa-max-usuarios" />
            </div>
            <div className="space-y-1.5">
              <Label>Plantilla de colores</Label>
              <Select
                value={editForm.plantilla_colores_id || "default"}
                onValueChange={(v) => setEditForm({ ...editForm, plantilla_colores_id: v === "default" ? "" : v })}
              >
                <SelectTrigger data-testid="edit-empresa-plantilla">
                  <SelectValue placeholder="Por defecto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Por defecto</SelectItem>
                  {plantillas.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.nombre}{p.es_default ? " (sistema)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">Identidad visual en modo claro y oscuro para los usuarios de la empresa.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Inicio periodo de uso</Label>
                <Input type="date" value={editForm.vigencia_inicio} onChange={(e) => setEditForm({ ...editForm, vigencia_inicio: e.target.value })} data-testid="edit-empresa-vigencia-inicio" />
              </div>
              <div className="space-y-1.5">
                <Label>Fin periodo de uso</Label>
                <Input type="date" value={editForm.vigencia_fin} onChange={(e) => setEditForm({ ...editForm, vigencia_fin: e.target.value })} data-testid="edit-empresa-vigencia-fin" />
              </div>
            </div>
            <div className="rounded-lg border border-zinc-200 p-3 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Administrador</p>
              <p className="text-xs text-zinc-500 leading-relaxed">
                {editFor?.gestionar_gerencia
                  ? "Con Grupo Gerencia activo, el administrador es cuenta de gestión (no cuenta en el cupo ni tiene ficha). Si cambias el correo, el actual pasa a trabajador conservando su ficha y se crea uno nuevo."
                  : "Sin Grupo Gerencia, el administrador puede ser también trabajador con ficha. Cambiar el correo solo actualiza el acceso del mismo usuario."}
              </p>
              <div className="space-y-1.5">
                <Label>Correo administrador</Label>
                <Input type="email" value={editForm.admin_email} onChange={(e) => setEditForm({ ...editForm, admin_email: e.target.value })} data-testid="edit-empresa-admin-email" placeholder="admin@empresa.es" />
              </div>
              <div className="space-y-1.5">
                <Label>
                  {(editForm.admin_email || "").toLowerCase().trim() !== (editFor?.admin_email || "").toLowerCase().trim()
                    && editFor?.gestionar_gerencia
                    ? "Contraseña del nuevo administrador"
                    : "Nueva contraseña"}
                </Label>
                <Input
                  type="password"
                  value={editForm.admin_password}
                  onChange={(e) => setEditForm({ ...editForm, admin_password: e.target.value })}
                  data-testid="edit-empresa-admin-password"
                  placeholder={
                    (editForm.admin_email || "").toLowerCase().trim() !== (editFor?.admin_email || "").toLowerCase().trim()
                    && editFor?.gestionar_gerencia
                      ? "Obligatoria al cambiar el correo"
                      : "Dejar vacío para no cambiar"
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Logo corporativo</Label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <LogoEmpresa url={logoPreview.startsWith("blob:") ? logoPreview : `${logoPreview}?v=${editFor?.id}`} className="h-16 w-16" />
                )}
                <Input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onLogoChange} data-testid="edit-empresa-logo" />
              </div>
              <p className="text-xs text-zinc-500">PNG, JPG, WEBP o GIF. Máximo 2 MB.</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={guardarEdicion}
              disabled={!editForm.nombre.trim() || !editForm.vigencia_inicio || !editForm.vigencia_fin}
              data-testid="edit-empresa-guardar-btn"
              className="bg-zinc-950 hover:bg-zinc-800"
            >
              Guardar cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adminFor} onOpenChange={(v) => !v && setAdminFor(null)}>
        <DialogContent data-testid="admin-empresa-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Crear administrador — {adminFor?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nombre</Label>
                <Input value={adminForm.nombre} onChange={(e) => setAdminForm({ ...adminForm, nombre: e.target.value })} data-testid="admin-nombre-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Apellidos</Label>
                <Input value={adminForm.apellidos} onChange={(e) => setAdminForm({ ...adminForm, apellidos: e.target.value })} data-testid="admin-apellidos-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} data-testid="admin-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Contraseña</Label>
              <Input type="password" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} data-testid="admin-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={crearAdmin}
              disabled={!adminForm.email || adminForm.password.length < 6}
              data-testid="admin-guardar-btn"
              className="bg-zinc-950 hover:bg-zinc-800"
            >
              Crear administrador
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!kioskFor} onOpenChange={(v) => !v && cerrarKiosk()}>
        <DialogContent
          data-testid="empresa-kiosk-dialog"
          className="max-h-[92vh] w-[min(96vw,56rem)] max-w-4xl overflow-y-auto p-6 sm:p-8"
        >
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-xl">
              <Tablet className="h-5 w-5 text-violet-600" strokeWidth={1.5} />
              Kiosko — {kioskFor?.nombre}
            </DialogTitle>
          </DialogHeader>
          {cargandoKiosk ? (
            <p className="py-12 text-center text-sm text-zinc-500">Cargando…</p>
          ) : kioskData?.token ? (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm">Enlace del quiosco web (tablet con navegador)</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={kioskWebUrl}
                    className="min-w-0 flex-1 font-mono text-sm"
                    data-testid="sa-quiosco-url"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline"
                    className="shrink-0 gap-1.5 rounded-lg"
                    onClick={() => copiarKiosk(kioskWebUrl, "URL web")}
                  >
                    <Copy className="h-4 w-4" strokeWidth={1.5} />
                    Copiar
                  </Button>
                </div>
              </div>

              <div className="rounded-2xl border border-violet-100 bg-violet-50/50 p-6">
                <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-start">
                  <div className="min-w-0 space-y-4">
                    <div className="flex items-center gap-2">
                      <QrCode className="h-5 w-5 text-violet-600" strokeWidth={1.5} />
                      <h3 className="text-base font-semibold text-zinc-900">App Android NFC</h3>
                    </div>
                    <p className="text-sm text-zinc-600">
                      Escanea el QR desde la app <b>Quiosco NFC</b> (Ajustes → Escanear QR).
                      La URL API debe ser la del backend (no localhost en la tablet).
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="sa-kiosk-api" className="text-sm">URL API para la app</Label>
                      <Input
                        id="sa-kiosk-api"
                        value={kioskApiUrl}
                        onChange={(e) => setKioskApiUrl(e.target.value)}
                        className="font-mono text-sm"
                        data-testid="sa-quiosco-api-url"
                        onFocus={(e) => e.target.select()}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Token de quiosco</Label>
                      <div className="flex gap-2">
                        <Input
                          readOnly
                          value={kioskData.token}
                          className="min-w-0 flex-1 font-mono text-sm"
                          data-testid="sa-quiosco-token"
                          onFocus={(e) => e.target.select()}
                        />
                        <Button
                          variant="outline"
                          className="shrink-0 gap-1.5 rounded-lg"
                          onClick={() => copiarKiosk(kioskData.token, "Token")}
                        >
                          <Copy className="h-4 w-4" strokeWidth={1.5} />
                          Copiar
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center lg:justify-end">
                    <KioskQrCode apiUrl={kioskApiUrl} token={kioskData.token} size={220} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-zinc-500">
              El kiosko no está activo. El administrador de la empresa lo activa en Ajustes → Quiosco.
            </p>
          )}
          <DialogFooter className="sm:justify-end">
            <Button variant="outline" onClick={cerrarKiosk} className="rounded-lg">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
